import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { toast } from "@redotech/redo-web/alert";
import { memo, useRef, useState } from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";
import { MergeModal } from "./merge-modal";

export interface MergeSuggestionSummary {
  mergeSuggestionId: string;
  groupCount: number;
  groupIds: string[];
}

export const MergeSuggestionModal = memo(function MergeSuggestionModal({
  summaries,
  open,
  setOpen,
  triggerReload,
  rejectedSuggestionIds,
  setRejectedSuggestionIds,
}: {
  summaries: MergeSuggestionSummary[] | undefined;
  open: boolean;
  setOpen: (val: boolean) => void;
  triggerReload: () => void;
  rejectedSuggestionIds: string[];
  setRejectedSuggestionIds: (ids: string[]) => void;
}) {
  const rpcClient = useRequiredContext(RedoOutboundLabelsRpcClientContext);

  const [needsRefresh, setNeedsRefresh] = useState<boolean>(false);
  const [suggestedIndex, setSuggestedIndex] = useState<number>(0);
  const idsPendingRejectionRef = useRef<string[]>([]);

  const suggestionList = summaries ?? [];
  const totalSuggestions = suggestionList.length;
  const currentSuggestionId =
    suggestionList[suggestedIndex]?.mergeSuggestionId ?? null;

  // Only the suggestion on screen is hydrated; the list itself carries counts
  // and group ids from the search index.
  const groupsLoad = useLoad(
    async (signal) => {
      if (!open || !currentSuggestionId) {
        return [];
      }
      const { groups } = await rpcClient.getMergeSuggestionGroups(
        { mergeSuggestionId: currentSuggestionId },
        { signal },
      );
      return groups;
    },
    [open, currentSuggestionId, rpcClient],
  );
  const groups = groupsLoad.value ?? [];

  const updateIdsPendingRejection = (newIds: string[]) => {
    idsPendingRejectionRef.current = newIds;
  };

  const [_, rejectSuggestion] = useTriggerLoad(async (signal) => {
    setRejectedSuggestionIds([
      ...rejectedSuggestionIds,
      ...idsPendingRejectionRef.current,
    ]);

    const rejectedGroupIds = suggestionList
      .filter((summary) =>
        idsPendingRejectionRef.current.includes(summary.mergeSuggestionId),
      )
      .flatMap((summary) => summary.groupIds);

    if (!rpcClient || !rejectedGroupIds.length) {
      return;
    }

    try {
      await rpcClient.rejectMergeSuggestion(
        { groupIds: rejectedGroupIds },
        { signal },
      );
    } catch (e) {
      toast("Unable to dismiss merge suggestion", { variant: "error" });
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      console.error((e as Error).message);
      setRejectedSuggestionIds(
        rejectedSuggestionIds.filter(
          (id) => !idsPendingRejectionRef.current.includes(id),
        ),
      );
    } finally {
      updateIdsPendingRejection([]);
    }
  });

  const onClose = useHandler(() => {
    setSuggestedIndex(0);
    if (needsRefresh) {
      triggerReload();
      setNeedsRefresh(false);
    }
    if (idsPendingRejectionRef.current.length > 0) {
      rejectSuggestion();
    }
    setOpen(false);
  });

  const onNext = useHandler(() => {
    if (suggestedIndex < totalSuggestions - 1) {
      setSuggestedIndex((prev) => prev + 1);
    } else {
      onClose();
    }
  });

  const onSkip = useHandler(() => {
    onNext();
  });

  return (
    <MergeModal
      analyticsSource="suggestion"
      groups={groups}
      headerTitle={`Merge fulfillments (${suggestedIndex + 1} of ${totalSuggestions})`}
      loading={groupsLoad.pending}
      loadingCardCount={suggestionList[suggestedIndex]?.groupCount}
      onCancel={() => {
        if (currentSuggestionId) {
          updateIdsPendingRejection([
            ...idsPendingRejectionRef.current,
            currentSuggestionId,
          ]);
        }
        onNext();
      }}
      onModalClose={onClose}
      onSkip={onSkip}
      open={open}
      postMerge={async (group: FulfillmentGroupData) => {
        setNeedsRefresh(true);
        onNext();
      }}
    />
  );
});
